import { lazy } from 'react'
import MyAccountHeader from 'account/components/secondaryHeaders/MyAccountHeader'
import { AppPlugins, userAccountPrefix } from 'app/constants'
import Plugin from 'core/plugins/plugin'
import { isOpenCloud } from 'core/utils/helpers'
import { isSystemAdminUser } from 'core/utils/rbac'

// Lazy load all route components for optimal code splitting
const SsoManagementPage = lazy(() => import('account/components/ssoManagement/SsoManagementPage'))
const SystemStatusPage = lazy(() => import('./components/system-status'))
const CustomThemePage = lazy(() => import('./components/theme/CustomThemePage'))
const UserSettingsIndexPage = lazy(() => import('./components/user-settings/UserSettingsIndexPage'))
const UserGroupsListPage = lazy(
  () => import('account/components/userManagement/groups/UserGroupsListPage'),
)
const UserRolesListPage = lazy(
  () => import('account/components/userManagement/roles/UserRolesListPage'),
)
const TenantDetailsPage = lazy(
  () => import('account/components/userManagement/tenants/TenantDetailsPage'),
)
const DomainsListPage = lazy(
  () => import('account/components/userManagement/domains/DomainsListPage'),
)
const TenantsListPage = lazy(
  () => import('account/components/userManagement/tenants/TenantsListPage'),
)
const UsersListPage = lazy(() => import('account/components/userManagement/users/UsersListPage'))
const ApiServicesPage = lazy(() => import('k8s/components/apiAccess/ApiServicesPage'))

const ApplicationCredentialsListPage = lazy(
  () =>
    import('openstack/components/security/application-credentials/ApplicationCredentialsListPage'),
)
const ApplicationCredentialDetailsPage = lazy(
  () =>
    import(
      'openstack/components/security/application-credentials/ApplicationCredentialDetailsPage'
    ),
)

const AuditLogsPage = lazy(() => import('account/components/audit-logs/AuditLogsPage'))

const myAccountPlugin = new Plugin(AppPlugins.MyAccount, 'Settings', userAccountPrefix, 'cog')
myAccountPlugin.registerSecondaryHeader(MyAccountHeader)

myAccountPlugin.registerRoutes([
  {
    name: 'System Status',
    link: {
      path: '/status',
      exact: true,
    },
    component: SystemStatusPage,
  },
  {
    name: 'User Settings',
    link: {
      path: '/settings/:tab?',
      exact: true,
      default: true,
      defaultParams: { tab: 'profile' },
    },
    component: UserSettingsIndexPage,
  },
  {
    name: 'Domains',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin'],
    link: {
      path: '/access/domains/(add)?',
      exact: true,
    },
    component: DomainsListPage,
  },
  {
    name: 'Tenants',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin', 'customer_domain_admin'],
    link: {
      path: '/access/tenants/(add)?',
      exact: true,
    },
    component: TenantsListPage,
  },
  {
    name: 'Tenant Details',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin', 'customer_domain_admin'],
    link: { path: '/access/tenants/:id/:tab?', exact: true },
    component: TenantDetailsPage,
  },
  {
    name: 'Users',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin', 'customer_domain_admin', 'tenant_admin'],
    link: {
      path: '/access/users/(add)?',
      exact: true,
    },
    component: UsersListPage,
  },
  {
    name: 'Roles',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin', 'customer_domain_admin', 'tenant_admin'],
    link: {
      path: '/access/roles/:tab?/(add)?',
      navPath: '/access/roles/:tab',
      exact: true,
    },
    component: UserRolesListPage,
  },
  {
    name: 'Groups',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin', 'customer_domain_admin', 'tenant_admin'],
    link: {
      path: '/access/groups/(add)?',
      exact: true,
    },
    component: UserGroupsListPage,
  },
  {
    name: 'Enterprise SSO',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin'],
    link: {
      path: '/sso/:tab?/(edit||add)?/:id?',
      exact: true,
    },
    component: SsoManagementPage,
  },
  {
    name: 'Custom Theme',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin'],
    link: { path: '/theme', exact: true },
    component: CustomThemePage,
  },
  {
    name: 'API Access',
    link: { path: '/api-access/api-endpoints', exact: true },
    component: ApiServicesPage,
  },
  {
    name: 'Application Credentials',
    link: { path: '/api-access/application-credentials/(create)?', exact: true },
    component: ApplicationCredentialsListPage,
  },
  {
    name: 'Application Credential Details',
    link: { path: '/api-access/application-credentials/:id', exact: true },
    component: ApplicationCredentialDetailsPage,
  },
  {
    name: 'Audit Logs',
    requiredRoles: ['admin', 'cluster_admin', 'platform_admin'],
    link: { path: '/audit-logs', exact: true },
    component: AuditLogsPage,
  },
])

// These nav items are in active development but not shown in production.
export const navItems = [
  {
    name: 'User Settings',
    link: { path: '/settings/profile', definition: '/settings/:tab' },
    icon: 'users-cog',
  },
  {
    name: 'API Access',
    icon: 'key',
    link: { path: '/api-access/api-endpoints', definition: '/api-access/:tab' },
    nestedLinks: [
      {
        name: 'API Endpoints',
        link: { path: '/api-access/api-endpoints' },
      },
      {
        name: 'Application Credentials',
        link: {
          path: '/api-access/application-credentials',
        },
        bannedRoles: ['platform_admin', 'sp_admin'],
        requiredPermissions: [
          [
            'keystone',
            'identity.list_application_credentials:GET/HEAD:/v3/users/{user_id}/application_credentials',
          ],
          [
            'keystone',
            'identity.create_application_credential:POST:/v3/users/{user_id}/application_credentials',
          ],
          [
            'keystone',
            'identity.delete_application_credential:DELETE:/v3/users/{user_id}/application_credentials/{application_credential_id}',
          ],
        ],
      },
    ],
  },
  {
    name: 'Audit Logs',
    icon: 'magnifying-glass',
    link: { path: '/audit-logs', definition: '/audit-logs' },
    requiredRoles: ['admin', 'platform_admin'],
  },
  {
    name: 'Tenants and Users',
    link: { path: '/access/users', definition: '/access/:tab' },
    icon: 'users',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin', 'customer_domain_admin', 'tenant_admin'],
    nestedLinks: [
      {
        name: 'Domains',
        link: { path: '/access/domains' },
        // isHidden: () => !isSystemAdminUser(),
        requiredRoles: ['admin', 'platform_admin', 'sp_admin'],
      },
      {
        name: 'Tenants',
        link: { path: '/access/tenants' },
        requiredRoles: ['admin', 'platform_admin', 'sp_admin'],
      },
      {
        name: 'Users',
        link: { path: '/access/users' },
        requiredRoles: [
          'admin',
          'platform_admin',
          'sp_admin',
          'customer_domain_admin',
          'tenant_admin',
        ],
      },
      {
        name: 'Groups',
        link: { path: '/access/groups' },
        requiredRoles: [
          'admin',
          'platform_admin',
          'sp_admin',
          'customer_domain_admin',
          'tenant_admin',
        ],
      },
      {
        name: 'Roles',
        link: { path: '/access/roles/predefined', definition: '/access/roles/:tab?' },
        requiredRoles: [
          'admin',
          'platform_admin',
          'sp_admin',
          'customer_domain_admin',
          'tenant_admin',
        ],
      },
    ],
  },
  {
    name: 'Enterprise SSO',
    link: { path: '/sso/configure', definition: '/sso/:tab' },
    icon: 'address-book',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin'],
    requiredFeatures: (features) => {
      // if (!features || !features.experimental) {
      //   return false
      // }
      // // Legacy DU & DDU have different conditions
      // if (features.experimental.kplane) {
      //   // Show user the upgrade dialog to FT users on the SSO screen itself
      //   return true
      // }
      // return features.experimental.sso
      return true
    },
  },
  {
    name: 'Custom Theme',
    link: { path: '/theme' },
    icon: 'palette',
    requiredRoles: ['admin', 'platform_admin', 'sp_admin'],
    requiredFeatures: (features) => {
      return !isOpenCloud()
    },
  },
]

myAccountPlugin.registerNavItems(navItems)

export default myAccountPlugin
